import styled from "styled-components";
import Modal from "common/Modal/Modal";
import { Security } from "common/Icons/Icons";
import Text from "common/Typography/Text";
import Button from "common/Buttons/Button.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  ExternalInvitationType,
  InvitationStatus,
  UserImpersonationPermissionLevel,
  WorkspaceType,
} from "@shared/types";
import { usePollClientsListData } from "utils/hooks/misc.tsx";
import { useContext } from "react";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace.ts";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context.ts";
import { useIframeSdk } from "@whop/react";
import { useGetExternalInviteLink } from "features/configuration/InviteExternalUsers/hooks.tsx";

interface WhopCreateAccountModalProps {
  onClose: () => void;
  open?: boolean;
  primaryExchange?: string;
}

const WhopCreateAccountModal = ({
  open,
  onClose,
  primaryExchange,
}: WhopCreateAccountModalProps) => {
  const [getAccountInvites, getAccountInvitesStatus] =
    sharedLib.sharedApi.configuration.useLazyGetAccountInvitesQuery();
  const [createAccountInvite, createAccountInviteStatus] =
    sharedLib.sharedApi.configuration.useCreateAccountInviteMutation();
  const { startPolling } = usePollClientsListData();
  const { setWorkspaceType, workspace } = useSharedWorkspace({});
  const { closeDrawer } = useContext(QuickConnectContext) || {};
  const iframeSdk = useIframeSdk();
  const { getExternalInviteLink } = useGetExternalInviteLink();

  const redirectToInvitation = (token: string) => {
    const inviteLink = getExternalInviteLink(token, primaryExchange);
    iframeSdk.openExternalUrl({ url: inviteLink, newTab: true });
    startPolling();
    onClose();
    if (workspace === WorkspaceType.DEMO) {
      setWorkspaceType(WorkspaceType.USER);
      closeDrawer?.();
    }
  };

  const onContinueClick = () => {
    getAccountInvites()
      .then((result) => {
        const validInvitation = result.data?.find((invitation) => {
          return (
            invitation.status === InvitationStatus.PENDING &&
            invitation.permission_level ===
              UserImpersonationPermissionLevel.NONE &&
            invitation.type === ExternalInvitationType.PERSONAL &&
            invitation.valid_to > Date.now() / 1000 + HALF_DAY_SECONDS
          );
        });
        if (validInvitation) {
          redirectToInvitation(validInvitation.token);
        } else {
          createAccountInvite({
            permission_level: UserImpersonationPermissionLevel.NONE,
            type: ExternalInvitationType.PERSONAL,
          })
            .unwrap()
            .then((invitation) => {
              redirectToInvitation(invitation.token);
            });
        }
      })
      .catch(() => {});
  };

  const isLoading =
    getAccountInvitesStatus.isLoading || createAccountInviteStatus.isLoading;

  return (
    <Modal
      onCancel={onClose}
      open={open}
      width={400}
      centered={true}
      footer={false}
      title="Secure Account Creation"
      destroyOnHidden={true}
    >
      <Wrapper>
        <Security
          style={{ marginBottom: 8, width: ICON_SIZE, height: ICON_SIZE }}
        />
        <div>
          <Text>
            We'll open a new tab to securely connect your exchange account.
          </Text>
          <Text>Once you're done, return here to continue.</Text>
        </div>
        <Button
          type="primary"
          $size="l"
          style={{ marginTop: 20 }}
          onClick={onContinueClick}
          loading={isLoading}
        >
          Continue to Secure Setup
        </Button>
      </Wrapper>
    </Modal>
  );
};

export default WhopCreateAccountModal;

const ICON_SIZE = 150;
const HALF_DAY_SECONDS = 12 * 60 * 60;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
